<template>
  <div class="public-wiki-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3">Loading wiki page...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">
        <i class="fa fa-exclamation-circle"></i>
      </div>
      <h3>Page Not Available</h3>
      <p>{{ errorMessage }}</p>
    </div>

    <!-- Wiki Content -->
    <div v-else-if="wikiPage" class="wiki-content-wrapper">
      <div class="wiki-header">
        <h1 class="wiki-title">{{ wikiPageTitle }}</h1>
      </div>
      <div class="wiki-body">
        <div
          v-for="(block, index) in contentBlocks"
          :key="index"
          class="content-block"
        >
          <!-- Header Block -->
          <component
            v-if="block.type === 'header'"
            :is="'h' + (block.data.level || 2)"
            class="wiki-header-block"
            :style="block.data.alignment ? { textAlign: block.data.alignment } : null"
            v-html="block.data.text"
          ></component>

          <!-- Paragraph Block -->
          <p
            v-else-if="block.type === 'paragraph'"
            class="wiki-paragraph"
            :style="block.data.alignment ? { textAlign: block.data.alignment } : null"
            v-html="block.data.text"
          ></p>

          <!-- Image Block -->
          <div v-else-if="block.type === 'image'" class="wiki-image-block">
            <img
              :src="block.data.file?.url || block.data.url"
              :alt="block.data.caption || 'Wiki image'"
              class="wiki-image"
            />
            <p v-if="block.data.caption" class="image-caption">{{ block.data.caption }}</p>
          </div>

          <!-- List Block -->
          <component
            v-else-if="block.type === 'list'"
            :is="block.data.style === 'ordered' ? 'ol' : 'ul'"
            class="wiki-list"
            v-bind="block.data.start ? { start: block.data.start } : {}"
          >
            <li v-for="(item, idx) in block.data.items" :key="idx" v-html="item"></li>
          </component>

          <!-- Table Block -->
          <div v-else-if="block.type === 'table'" class="wiki-table-wrapper">
            <table class="wiki-table">
              <thead v-if="block.data.withHeadings && block.data.content.length > 0">
                <tr>
                  <th v-for="(cell, cellIdx) in block.data.content[0]" :key="cellIdx" v-html="cell"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIdx) in (block.data.withHeadings ? block.data.content.slice(1) : block.data.content)" :key="rowIdx">
                  <td v-for="(cell, cellIdx) in row" :key="cellIdx" v-html="cell"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Delimiter Block -->
          <hr v-else-if="block.type === 'delimiter'" class="wiki-delimiter" />

          <!-- Checklist Block -->
          <div v-else-if="block.type === 'checklist'" class="wiki-checklist">
            <div
              v-for="(item, idx) in block.data.items"
              :key="idx"
              class="checklist-item"
            >
              <input type="checkbox" :checked="item.checked" disabled />
              <span v-html="item.text"></span>
            </div>
          </div>

          <!-- Embed Block (YouTube, etc.) -->
          <div v-else-if="block.type === 'embed'" class="wiki-embed-block">
            <div class="embed-wrapper">
              <iframe
                :src="block.data.embed"
                :width="block.data.width || 600"
                :height="block.data.height || 320"
                frameborder="0"
                allowfullscreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              ></iframe>
            </div>
            <p v-if="block.data.caption" class="embed-caption">{{ block.data.caption }}</p>
          </div>
        </div>
      </div>

      <PoweredByFooter />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import { mapActions } from 'vuex';
import PoweredByFooter from '@/components/PoweredByFooter.vue';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
  name: 'PublicWikiView',
  components: { PoweredByFooter },
  props: ['wikiPageID'],

  data() {
    return {
      wikiPage: null,
      loading: true,
      error: false,
      errorMessage: 'This page is not available or is not public.',
      currentLanguage: 'en'  // Default to English, will check URL param
    };
  },

  async created() {
    // Check for language preference from URL param
    this.currentLanguage = this.$route.query.lang === 'es' ? 'es' : 'en';
    await this.loadPublicWiki();
  },

  computed: {
    wikiPageTitle() {
      if (!this.wikiPage || !this.wikiPage.Name) return '';
      return this.wikiPage.Name;
    },

    contentBlocks() {
      if (!this.wikiPage || !this.wikiPage.Content) return [];
      try {
        const content = JSON.parse(this.wikiPage.Content);
        // Skip the first block if it's a header (used as title)
        if (content.blocks && content.blocks.length > 0 && content.blocks[0].type === 'header') {
          return content.blocks.slice(1);
        }
        return content.blocks || [];
      } catch (e) {
        console.error('Error parsing wiki content:', e);
        return [];
      }
    }
  },

  methods: {
    ...mapActions('wiki', ['getPublicWiki']),

    async loadPublicWiki() {
      try {
        this.loading = true;
        this.error = false;

        // Pass language preference to the API
        const response = await this.getPublicWiki({
          wikiPageID: this.wikiPageID,
          lang: this.currentLanguage
        });

        if (response) {
          this.wikiPage = response;
        } else {
          this.error = true;
          this.errorMessage = 'This page is not available or is not public.';
        }
      } catch (err) {
        console.error('Error loading public wiki:', err);
        this.error = true;
        if (err.response && err.response.status === 404) {
          this.errorMessage = 'This wiki page does not exist.';
        } else if (err.response && err.response.status === 403) {
          this.errorMessage = 'This wiki page is not publicly accessible.';
        } else {
          this.errorMessage = 'Unable to load this wiki page. Please try again later.';
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.public-wiki-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: #fff;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #666;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  color: #666;

  .error-icon {
    font-size: 64px;
    color: #dc3545;
    margin-bottom: 20px;
  }

  h3 {
    color: #333;
    margin-bottom: 10px;
  }

  p {
    color: #666;
    max-width: 400px;
  }
}

.wiki-content-wrapper {
  .wiki-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #eee;
  }

  .wiki-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #333;
    margin: 0;
  }
}

.wiki-body {
  .content-block {
    margin-bottom: 20px;
  }

  .wiki-header-block {
    color: #333;
    margin-top: 1.5em;
    margin-bottom: 0.5em;

    &:first-child {
      margin-top: 0;
    }
  }

  .wiki-paragraph {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #444;

    a {
      color: #007bff;
      text-decoration: underline;

      &:hover {
        color: #0056b3;
      }
    }
  }

  .wiki-image-block {
    margin: 30px 0;
    text-align: center;

    .wiki-image {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .image-caption {
      margin-top: 10px;
      font-size: 0.9rem;
      color: #666;
      font-style: italic;
    }
  }

  .wiki-list {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #444;
    padding-left: 25px;

    li {
      margin-bottom: 8px;
    }
  }

  .wiki-table-wrapper {
    overflow-x: auto;
    margin: 20px 0;
  }

  .wiki-table {
    width: 100%;
    border-collapse: collapse;

    th {
      padding: 12px 15px;
      border: 1px solid #ccc;
      font-size: 1rem;
      font-weight: 600;
      background: #f0f0f0;
      text-align: left;
    }

    td {
      padding: 12px 15px;
      border: 1px solid #ddd;
      font-size: 1rem;
    }

    tbody tr:nth-child(even) {
      background: #f9f9f9;
    }
  }

  .wiki-delimiter {
    margin: 40px auto;
    width: 50%;
    border: none;
    border-top: 2px solid #eee;
  }

  .wiki-checklist {
    .checklist-item {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 8px;
      font-size: 1.1rem;
      color: #444;

      input[type="checkbox"] {
        width: 18px;
        height: 18px;
      }
    }
  }

  .wiki-embed-block {
    margin: 30px 0;
    text-align: center;

    .embed-wrapper {
      position: relative;
      padding-bottom: 56.25%; /* 16:9 aspect ratio */
      height: 0;
      overflow: hidden;
      max-width: 100%;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 8px;
      }
    }

    .embed-caption {
      margin-top: 10px;
      font-size: 0.9rem;
      color: #666;
      font-style: italic;
    }
  }
}


@media (max-width: 768px) {
  .public-wiki-container {
    padding: 20px 15px;
  }

  .wiki-title {
    font-size: 1.8rem !important;
  }

  .wiki-paragraph,
  .wiki-list {
    font-size: 1rem !important;
  }
}
</style>
